import React from 'react';
import { cn } from '@/lib/utils';

/**
 * CoRegulate Button Components - Revamped
 * 
 * Dynamic yet grounding buttons with:
 * - Subtle breathing animations
 * - Warm, earthy color palette
 * - Gentle glow effects
 * - Smooth, calming transitions
 * - No jarring or demanding interactions
 */

// Base button styles with smooth transitions
const baseStyles = `
  inline-flex items-center justify-center gap-2
  font-medium transition-all duration-700 ease-out
  focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent
  disabled:opacity-50 disabled:cursor-not-allowed
  active:scale-[0.98]
  relative overflow-hidden
`;

// Size variants - all use pill shape (rounded-full)
const sizeStyles = {
  sm: 'h-10 px-6 text-sm rounded-full',
  md: 'h-12 px-8 text-base rounded-full',
  action: 'h-11 px-7 text-base rounded-full',
  lg: 'h-14 px-10 text-lg rounded-full',
  xl: 'h-16 px-12 text-lg rounded-full',
};

/**
 * Breathing glow overlay - subtle pulse animation
 */
const BreathingGlow = ({ color = 'rgba(255,187,152,0.3)' }) => (
  <div 
    className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
    style={{
      background: `radial-gradient(circle at center, ${color} 0%, transparent 70%)`,
      animation: 'buttonBreathe 3s ease-in-out infinite',
    }}
  />
);

/**
 * Warm shine overlay for hover
 */
const WarmShine = () => (
  <div 
    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none rounded-full"
    style={{
      background: 'linear-gradient(120deg, transparent 30%, rgba(255,250,240,0.25) 50%, transparent 70%)',
      transform: 'translateX(-100%)',
      animation: 'warmShine 2s ease-in-out infinite',
    }}
  />
);

/**
 * CSS for button animations - inject once
 */
const ButtonStyles = () => (
  <style>{`
    @keyframes buttonBreathe {
      0%, 100% {
        transform: scale(1);
        opacity: 0.4;
      }
      50% {
        transform: scale(1.05);
        opacity: 0.6;
      }
    }
    
    @keyframes warmShine {
      0% {
        transform: translateX(-100%);
      }
      50%, 100% {
        transform: translateX(100%);
      }
    }
    
    @keyframes gentlePulse {
      0%, 100% {
        box-shadow: 0 4px 20px rgba(255,187,152,0.25);
      }
      50% {
        box-shadow: 0 6px 28px rgba(255,187,152,0.35);
      }
    }
    
    @keyframes subtleFloat {
      0%, 100% {
        transform: translateY(0);
      }
      50% {
        transform: translateY(-2px);
      }
    }
  `}</style>
);

/**
 * Primary Button - Gentle with subtle animation
 * Sage green gradient (original)
 */
export const PrimaryButton = React.forwardRef(({
  children,
  className,
  size = 'md',
  variant = 'sage', // 'sage' | 'eucalyptus' | 'rose' | 'blue' | 'lavender'
  disabled,
  animate = true,
  ...props
}, ref) => {
  // Sunset Lake palette gradients - rich, warm feel
  const variantStyles = {
    sage: {
      background: 'linear-gradient(145deg, #2A8585 0%, #1D6B6B 50%, #145555 100%)',
      shadow: '0 4px 20px rgba(52,70,72,0.3)',
      hoverShadow: '0 8px 32px rgba(52,70,72,0.45)',
      glow: 'rgba(52,70,72,0.4)',
      ring: '#1D6B6B',
      text: 'text-white'
    },
    eucalyptus: {
      background: 'linear-gradient(145deg, #3A9595 0%, #2A8585 50%, #1D6B6B 100%)',
      shadow: '0 4px 20px rgba(52,70,72,0.3)',
      hoverShadow: '0 8px 32px rgba(52,70,72,0.45)',
      glow: 'rgba(93,110,117,0.4)',
      ring: '#2A8585',
      text: 'text-white'
    },
    rose: {
      background: 'linear-gradient(145deg, #FFD0B0 0%, #FFBB98 50%, #E09060 100%)',
      shadow: '0 4px 20px rgba(255,187,152,0.35)',
      hoverShadow: '0 8px 32px rgba(255,187,152,0.5)',
      glow: 'rgba(255,187,152,0.4)',
      ring: '#FFBB98',
      text: 'text-[#1D6B6B]'
    },
    blue: {
      background: 'linear-gradient(145deg, #9AABB8 0%, #7D8E95 50%, #3A9595 100%)',
      shadow: '0 4px 20px rgba(125,142,149,0.3)',
      hoverShadow: '0 8px 32px rgba(125,142,149,0.45)',
      glow: 'rgba(125,142,149,0.4)',
      ring: '#7D8E95',
      text: 'text-white'
    },
    lavender: {
      background: 'linear-gradient(145deg, #9AABB8 0%, #7D8E95 50%, #3A9595 100%)',
      shadow: '0 4px 20px rgba(125,142,149,0.3)',
      hoverShadow: '0 8px 32px rgba(125,142,149,0.45)',
      glow: 'rgba(125,142,149,0.4)',
      ring: '#7D8E95',
      text: 'text-white'
    }
  };

  const styles = variantStyles[variant] || variantStyles.sage;

  return (
    <>
      <ButtonStyles />
      <button
        ref={ref}
        disabled={disabled}
        className={cn(
          baseStyles,
          sizeStyles[size],
          styles.text,
          'group',
          animate && !disabled && 'hover:scale-[1.03]',
          className
        )}
        style={{
          background: styles.background,
          boxShadow: styles.shadow,
          animation: animate && !disabled ? 'gentlePulse 4s ease-in-out infinite' : 'none',
        }}
        onMouseEnter={(e) => {
          if (!disabled) {
            e.currentTarget.style.boxShadow = styles.hoverShadow;
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = styles.shadow;
        }}
        {...props}
      >
        <span className="relative z-10 flex items-center justify-center gap-2">
          {children}
        </span>
        <BreathingGlow color={styles.glow} />
        <WarmShine />
      </button>
    </>
  );
});
PrimaryButton.displayName = 'PrimaryButton';

/**
 * Secondary Button - Glass-like with soft sage tint
 */
export const SecondaryButton = React.forwardRef(({
  children,
  className,
  size = 'md',
  disabled,
  ...props
}, ref) => {
  return (
    <>
      <ButtonStyles />
      <button
        ref={ref}
        disabled={disabled}
        className={cn(
          baseStyles,
          sizeStyles[size],
          'group hover:scale-[1.02]',
          'text-[#2A8585]',
          className
        )}
        style={{
          background: 'rgba(255,255,255,0.7)',
          border: '1.5px solid rgba(52,70,72,0.4)',
          boxShadow: '0 2px 12px rgba(52,70,72,0.08)',
          backdropFilter: 'blur(8px)',
        }}
        onMouseEnter={(e) => {
          if (!disabled) {
            e.currentTarget.style.background = 'rgba(52,70,72,0.12)';
            e.currentTarget.style.borderColor = 'rgba(52,70,72,0.6)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(52,70,72,0.12)';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.7)';
          e.currentTarget.style.borderColor = 'rgba(52,70,72,0.4)';
          e.currentTarget.style.boxShadow = '0 2px 12px rgba(52,70,72,0.08)';
        }}
        {...props}
      >
        <span className="relative z-10 flex items-center justify-center gap-2">
          {children}
        </span>
      </button>
    </>
  );
});
SecondaryButton.displayName = 'SecondaryButton';

/**
 * Tertiary Button - Text-only with gentle hover
 */
export const TertiaryButton = React.forwardRef(({
  children,
  className,
  size = 'md',
  underline = false,
  disabled,
  ...props
}, ref) => {
  const sizeMap = {
    sm: 'text-sm px-4 py-2',
    md: 'text-base px-5 py-2.5',
    lg: 'text-lg px-6 py-3',
  };

  return (
    <button
      ref={ref}
      disabled={disabled}
      className={cn(
        'inline-flex items-center justify-center gap-2',
        'font-medium transition-all duration-500 ease-out',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#7D8E95]/50',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'active:scale-[0.98]',
        'rounded-full',
        sizeMap[size],
        'bg-transparent text-[#2A8585]',
        'hover:text-[#1D6B6B] hover:bg-[#7D8E95]/10',
        underline && 'underline underline-offset-4 decoration-[#7D8E95] hover:decoration-[#7D8E95]',
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
});
TertiaryButton.displayName = 'TertiaryButton';

/**
 * Icon Button - Circular with sage hover
 */
export const IconButton = React.forwardRef(({
  children,
  className,
  size = 'md',
  variant = 'ghost', // 'ghost' | 'filled' | 'outline' | 'primary'
  disabled,
  ...props
}, ref) => {
  const sizeMap = {
    sm: 'w-9 h-9',
    md: 'w-11 h-11',
    lg: 'w-13 h-13',
  };

  const variantStyles = {
    ghost: {
      className: 'bg-transparent text-[#2A8585] hover:bg-[#7D8E95]/15 hover:text-[#1D6B6B]',
      style: {}
    },
    filled: {
      className: 'text-[#2A8585] hover:text-[#1D6B6B]',
      style: {
        background: 'rgba(255,255,255,0.7)',
        boxShadow: '0 2px 10px rgba(52,70,72,0.08)'
      }
    },
    outline: {
      className: 'bg-transparent text-[#2A8585] hover:bg-[#7D8E95]/10 hover:text-[#1D6B6B]',
      style: {
        border: '1.5px solid rgba(52,70,72,0.4)'
      }
    },
    primary: {
      className: 'text-white group',
      style: {
        background: 'linear-gradient(145deg, #7D8E95 0%, #1D6B6B 100%)',
        boxShadow: '0 3px 14px rgba(52,70,72,0.3)'
      }
    }
  };

  const v = variantStyles[variant] || variantStyles.ghost;

  return (
    <button
      ref={ref}
      disabled={disabled}
      className={cn(
        'inline-flex items-center justify-center',
        'transition-all duration-500 ease-out',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#7D8E95]/50',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'active:scale-[0.95]',
        'rounded-full p-0',
        'hover:scale-[1.08]',
        sizeMap[size],
        v.className,
        className
      )}
      style={v.style}
      {...props}
    >
      {children}
    </button>
  );
});
IconButton.displayName = 'IconButton';

/**
 * Stepper Button - For navigation flows
 */
export const StepperButton = React.forwardRef(({
  children,
  className,
  direction = 'next', // 'prev' | 'next'
  disabled,
  ...props
}, ref) => {
  const directionConfig = {
    prev: {
      className: 'text-[#2A8585] hover:text-[#1D6B6B]',
      style: {
        background: 'rgba(255,255,255,0.7)',
        border: '1.5px solid rgba(52,70,72,0.3)',
        boxShadow: '0 2px 10px rgba(52,70,72,0.06)'
      },
      hoverStyle: {
        background: 'rgba(52,70,72,0.1)',
        borderColor: 'rgba(52,70,72,0.5)'
      }
    },
    next: {
      className: 'text-white group',
      style: {
        background: 'linear-gradient(145deg, #7D8E95 0%, #1D6B6B 100%)',
        boxShadow: '0 4px 18px rgba(52,70,72,0.35)'
      },
      hoverStyle: {
        boxShadow: '0 6px 26px rgba(52,70,72,0.5)'
      }
    }
  };

  const config = directionConfig[direction];

  return (
    <>
      <ButtonStyles />
      <button
        ref={ref}
        disabled={disabled}
        className={cn(
          'inline-flex items-center justify-center',
          'transition-all duration-500 ease-out',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#1D6B6B]',
          'disabled:opacity-40 disabled:cursor-not-allowed',
          'active:scale-[0.95]',
          'w-13 h-13 rounded-full p-0',
          'hover:scale-[1.08]',
          config.className,
          className
        )}
        style={config.style}
        onMouseEnter={(e) => {
          if (!disabled && config.hoverStyle) {
            Object.assign(e.currentTarget.style, config.hoverStyle);
          }
        }}
        onMouseLeave={(e) => {
          if (config.style) {
            Object.assign(e.currentTarget.style, config.style);
          }
        }}
        {...props}
      >
        {children}
      </button>
    </>
  );
});
StepperButton.displayName = 'StepperButton';

/**
 * Generic Brand Button - Flexible variant prop
 */
export const BrandButton = React.forwardRef(({
  children,
  className,
  variant = 'primary', // 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'danger'
  size = 'md',
  disabled,
  ...props
}, ref) => {
  const variantConfig = {
    primary: {
      className: 'text-white group hover:scale-[1.03]',
      style: {
        background: 'linear-gradient(145deg, #9CB88F 0%, #1D6B6B 40%, #1D6B6B 100%)',
        boxShadow: '0 4px 20px rgba(52,70,72,0.25)'
      },
      hoverStyle: {
        boxShadow: '0 8px 32px rgba(52,70,72,0.4)'
      },
      hasEffects: true
    },
    secondary: {
      className: 'text-[#2A8585] hover:scale-[1.02]',
      style: {
        background: 'rgba(255,255,255,0.7)',
        border: '1.5px solid rgba(52,70,72,0.4)',
        boxShadow: '0 2px 12px rgba(52,70,72,0.08)'
      },
      hoverStyle: {
        background: 'rgba(52,70,72,0.12)',
        borderColor: 'rgba(52,70,72,0.6)',
        boxShadow: '0 4px 20px rgba(52,70,72,0.12)'
      }
    },
    tertiary: {
      className: 'text-[#2A8585] hover:text-[#1D6B6B] hover:bg-[#7D8E95]/10',
      style: {
        background: 'transparent'
      }
    },
    ghost: {
      className: 'text-[#2A8585] hover:bg-[#7D8E95]/15',
      style: {
        background: 'transparent'
      }
    },
    danger: {
      className: 'text-white group hover:scale-[1.03]',
      style: {
        background: 'linear-gradient(145deg, #D4A8A0 0%, #C09088 40%, #A87870 100%)',
        boxShadow: '0 4px 20px rgba(255,187,152,0.3)'
      },
      hoverStyle: {
        boxShadow: '0 8px 32px rgba(255,187,152,0.45)'
      },
      hasEffects: true
    }
  };

  const config = variantConfig[variant] || variantConfig.primary;

  return (
    <>
      <ButtonStyles />
      <button
        ref={ref}
        disabled={disabled}
        className={cn(
          baseStyles,
          sizeStyles[size],
          config.className,
          className
        )}
        style={config.style}
        onMouseEnter={(e) => {
          if (!disabled && config.hoverStyle) {
            Object.assign(e.currentTarget.style, config.hoverStyle);
          }
        }}
        onMouseLeave={(e) => {
          if (config.style) {
            Object.assign(e.currentTarget.style, config.style);
          }
        }}
        {...props}
      >
        <span className="relative z-10 flex items-center justify-center gap-2">
          {children}
        </span>
        {config.hasEffects && (
          <>
            <BreathingGlow color="rgba(125,142,149,0.4)" />
            <WarmShine />
          </>
        )}
      </button>
    </>
  );
});
BrandButton.displayName = 'BrandButton';

export default BrandButton;
